import Config from "@/config";
import {RELEASE} from "@/constants/release";
import {detectEnvironment, type AppEnvironment} from "./detectEnvironment";

type EventParams = {
  [key: string]: string | number | boolean | null | undefined;
};

// Cached on first read — UA parsing isn't free and the runtime surface
// (PWA / in-app webview / browser) cannot change within a single page load.
let cachedAppEnvironment: AppEnvironment | undefined;

const getAppEnvironment = (): AppEnvironment => {
  cachedAppEnvironment ??= detectEnvironment();
  return cachedAppEnvironment;
};

// Default Consent Mode v2 state — denied until user grants consent in the UI.
// Shared with `useCookiesConsent` so the same set of keys is used for both
// the initial `consent default` call and later `consent update` calls.
// https://developers.google.com/tag-platform/security/guides/consent
export const DEFAULT_CONSENT = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
  functionality_storage: "denied",
  personalization_storage: "denied",
  security_storage: "denied",
} as const;

/**
 * Initializes method `window.gtag()` for Google Analytics.
 */
export const initGoogleTag = () => {
  try {
    // If the method already exists, do not initialize it again.
    if (typeof window.gtag === "function") {
      console.warn(
        "Method `window.gtag()` already exists, skipping initialization.",
      );
      return;
    }

    // Declare the global `window.dataLayer` array and the `window.gtag()` method.
    // https://developers.google.com/tag-platform/gtagjs
    window.dataLayer = window.dataLayer || [];

    function gtag(..._args: unknown[]) {
      // Push the arguments to the dataLayer array, which is used by Google Tag Manager.
      // gtag.js identifies a command call by the pushed value being an
      // Arguments object; pushing the rest-parameter array instead makes it
      // read as a plain data push and the command is silently ignored. This is
      // the shape Google's own snippet uses, so `arguments` has to stay.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    }

    window.gtag = gtag;
    gtag("js", new Date());

    // Set Consent Mode v2 defaults BEFORE any config or event call so the
    // first page_view is sent with a correct consent context.
    gtag("consent", "default", DEFAULT_CONSENT);

    gtag("config", Config.GOOGLE_ANALYTICS_ID, {
      debug_mode: Config.GOOGLE_ANALYTICS_DEBUG_MODE,
      // Disable automatic page view tracking, we will do it manually.
      // https://developers.google.com/analytics/devguides/collection/ga4/views?client_type=gtag#manual_pageviews
      send_page_view: false,
      // Use `navigator.sendBeacon` so events survive navigations, reloads and
      // tab close (important for in-app WebViews like Facebook/Instagram).
      transport_type: "beacon",
    });
  } catch (error) {
    console.error("Error in `window.gtag()` initialization:", error);
  }
};

/**
 * Strips all query parameters from a URL except for UTM parameters (keys starting with "utm_").
 * Explicitly removes URL hash/fragments for privacy/security reasons.
 */
export const getCleanUrl = (urlStr: string): string => {
  try {
    const url = new URL(urlStr);
    const newParams = new URLSearchParams();
    url.searchParams.forEach((value, key) => {
      if (key.toLowerCase().startsWith("utm_")) {
        newParams.set(key, value);
      }
    });
    url.search = newParams.toString();
    url.hash = "";
    return url.toString();
  } catch {
    return urlStr.split(/[?#]/)[0];
  }
};

/**
 * Load the Google tag script for Google Analytics.
 */
export const loadGoogleTagScript = () => {
  try {
    const existingScript = document.querySelector(
      'script[src*="googletagmanager.com/gtag/js"]',
    );

    // If the script already exists, do not initialize it again.
    if (existingScript) return;

    // Defer loading of gtag.js script to avoid render-blocking issues.
    const gtagScript = document.createElement("script");
    gtagScript.defer = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${Config.GOOGLE_ANALYTICS_ID}`;

    gtagScript.onerror = (error) => {
      console.error("Error loading gtag.js script:", error);
    };

    console.info("Appending gtag.js script to head...");
    document.head.appendChild(gtagScript);

    return gtagScript;
  } catch (error) {
    console.error("Error in gtag.js initialization:", error);
  }
};

/**
 * Logs an event to Google Analytics.
 */
export const logEvent = (eventName: string, eventParams?: EventParams) => {
  if (typeof window === "undefined") {
    return;
  }

  // Load the Google Tag script to ensure that the Google Analytics event is logged.
  loadGoogleTagScript();

  if (typeof window?.gtag !== "function") {
    console.warn(
      "Google Analytics not initialized properly. Event not logged:",
      eventName,
    );
    return;
  }

  try {
    window.gtag("event", eventName, {
      ...eventParams,
      app_environment: getAppEnvironment(),
      // After the spread, so a caller cannot shadow it -- same contract as
      // app_environment. Spread conditionally so a build that can name
      // neither a tag nor a commit omits the key rather than sending
      // `app_version: ""`, which in GA4 reads as "we shipped a build with no
      // version" rather than "we do not know which build this is".
      //
      // Note for whoever looks for this in a report: a GA4 event parameter
      // stays invisible until it is registered as a custom dimension in the GA
      // UI. Until that is done the value arrives and cannot be seen, which
      // looks exactly like this code not working.
      ...(RELEASE ? {app_version: RELEASE} : {}),
    });
  } catch (error) {
    console.warn("Failed to log event to Google Analytics:", error);
  }
};
